import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../context/auth-context.jsx';
import { adminApi } from '../../../lib/admin-api/index.js';
import {
  buildEmailCredentialsPayloadFromForm,
  mapEmailCredentialsFieldErrors,
  mapEmailCredentialsResponseToDto,
  validateEmailCredentialsPayload,
} from '../dto/system-email-settings.dto.js';
import { buildEmptyEmailCredentialsForm } from '../model/system-email-settings.model.js';

function formsEqual(left, right) {
  return ['fromAddress', 'fromName', 'host', 'port', 'username', 'password']
    .every((key) => String(left?.[key] || '') === String(right?.[key] || ''));
}

export function useSystemEmailSettings() {
  const { token } = useAuth();

  const [form, setForm] = useState(buildEmptyEmailCredentialsForm());
  const [initialForm, setInitialForm] = useState(buildEmptyEmailCredentialsForm());
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const loadCredentials = useCallback(async () => {
    setIsLoading(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = await adminApi.getEmailCredentials(token);
      const nextForm = mapEmailCredentialsResponseToDto(response);
      setForm(nextForm);
      setInitialForm(nextForm);
    } catch (requestError) {
      setForm(buildEmptyEmailCredentialsForm());
      setInitialForm(buildEmptyEmailCredentialsForm());
      setError(requestError.message || 'Failed to load email settings');
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadCredentials();
  }, [loadCredentials]);

  const onFormChange = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => {
      if (!prev[key]) {
        return prev;
      }

      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const resetForm = useCallback(() => {
    if (isSaving) {
      return;
    }

    setForm(initialForm);
    setFormErrors({});
    setError('');
    setSuccess('');
  }, [initialForm, isSaving]);

  const saveForm = useCallback(async (event) => {
    event.preventDefault();

    const payload = buildEmailCredentialsPayloadFromForm(form);
    const nextErrors = validateEmailCredentialsPayload(payload);

    if (Object.keys(nextErrors).length > 0) {
      setFormErrors(nextErrors);
      setError('Please fix validation errors.');
      setSuccess('');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    setFormErrors({});

    try {
      const response = await adminApi.updateEmailCredentials(token, {
        fromAddress: payload.fromAddress,
        fromName: payload.fromName,
        host: payload.host,
        port: payload.port,
        username: payload.username,
        password: payload.password,
      });
      const nextForm = mapEmailCredentialsResponseToDto(response);
      setForm(nextForm);
      setInitialForm(nextForm);
      setSuccess('Email settings updated.');
    } catch (requestError) {
      setFormErrors(mapEmailCredentialsFieldErrors(requestError));
      setError(requestError.message || 'Failed to update email settings');
    } finally {
      setIsSaving(false);
    }
  }, [form, token]);

  const hasChanges = !formsEqual(form, initialForm);

  return {
    form,
    isLoading,
    isSaving,
    error,
    success,
    formErrors,
    hasChanges,
    onFormChange,
    resetForm,
    saveForm,
  };
}
